import { IconCheck, IconX } from '@tabler/icons-react';
import React from 'react';
import { UserDto } from 'src/api/FiretrolApiClient';
import { Badge } from 'src/design/atoms/Badge';
import { Group } from 'src/design/atoms/Group';
import { Stack } from 'src/design/atoms/Stack';
import { Text } from 'src/design/atoms/Text';

type ChannelUser = Pick<
  UserDto,
  | 'notificationChannels'
  | 'smsConsentStatus'
  | 'phoneNumber'
  | 'phoneNumberVerifiedAt'
>;

// Mirrors the order of the backend SmsEligibilityEvaluator, so the first
// reason shown is the one that suppresses delivery.
const smsInactiveReason = (user: ChannelUser): string | undefined => {
  if (!(user.notificationChannels ?? []).includes('sms')) return 'Turned off';
  if (user.smsConsentStatus === 'opted_out') return 'Opted out';
  if (user.smsConsentStatus !== 'consented') return 'No SMS consent';
  if (!user.phoneNumber || !user.phoneNumberVerifiedAt) {
    return 'Phone not verified';
  }
  return undefined;
};

const ChannelRow = ({
  label,
  active,
  reason,
}: {
  label: string;
  active: boolean;
  reason?: string;
}) => (
  <Group gap="xs" wrap="nowrap" data-testid={`channel-${label}`}>
    <Text c="black" size="sm" w={100}>
      {label}
    </Text>
    <Badge
      color={active ? 'green' : 'gray'}
      variant="light"
      leftSection={active ? <IconCheck size={12} /> : <IconX size={12} />}
    >
      {active ? 'Active' : 'Inactive'}
    </Badge>
    {!active && reason && (
      <Text c="dimmed" size="xs">
        {reason}
      </Text>
    )}
  </Group>
);

export const NotificationChannelStatus = ({ user }: { user: ChannelUser }) => {
  const emailActive = (user.notificationChannels ?? []).includes('email');
  const smsReason = smsInactiveReason(user);

  return (
    <Stack gap={4}>
      <ChannelRow
        label="Email"
        active={emailActive}
        reason={emailActive ? undefined : 'Turned off'}
      />
      <ChannelRow
        label="Text Message"
        active={smsReason === undefined}
        reason={smsReason}
      />
    </Stack>
  );
};
